import { Trans, useTranslation } from "react-i18next";

import { CurrentStakeholderCard } from "src/core/components/folder/stakeholders/CurrentStakeholderCard";
import { DuplicateCandidateCard } from "src/core/components/folder/stakeholders/DuplicateCandidateCard";
import { Button } from "src/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/core/components/ui/dialog";
import { MappedStakeholder } from "src/core/constants/folder";
import { useStakeholderDuplicatesModal } from "src/core/hooks/folder/stakeholder-duplicates/useStakeholderDuplicatesModal";
import { useToast } from "src/core/hooks/use-toast";
import { getDuplicateCheckErrorCode } from "src/core/utils/stakeholderDuplicates.utils";

type StakeholderDuplicatesModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  stakeholder: MappedStakeholder;
};

export const StakeholderDuplicatesModal = ({
  open,
  setOpen,
  stakeholder,
}: StakeholderDuplicatesModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const {
    loading,
    countryWithRiskOptions,
    visibleCandidates,
    canGoPrev,
    canGoNext,
    hasSameCandidate,
    selectedReferenceProfile,
    customerCompaniesByEntity,
    getCandidateDecisionKey,
    resolveDuplicateSelection,
    candidateStartIndex,
    candidates,
    candidateDecisions,
    goToPreviousCandidate,
    goToNextCandidate,
    markCandidateAsSame,
    markCandidateAsDifferent,
    selectCurrentAsReference,
    selectCandidateAsReference,
  } = useStakeholderDuplicatesModal({
    open,
    stakeholder,
  });

  const handleSave = async () => {
    try {
      await resolveDuplicateSelection();

      toast({
        description: t("stakeholders.duplicateCheck.modal.form.success"),
      });
    } catch (error) {
      const errorCode = getDuplicateCheckErrorCode(error);

      if (errorCode) {
        toast({
          title: t("stakeholders.duplicateCheck.modal.form.error.title"),
          description: t(`stakeholders.duplicateCheck.modal.form.error.${errorCode}`),
          variant: "destructive",
        });
      } else {
        toast({
          description: t("stakeholders.duplicateCheck.modal.form.error.description"),
          variant: "destructive",
        });
      }
    } finally {
      setOpen(false);
    }
  };

  const hasOnlyOneDuplicateCandidate = candidates.length <= 1;
  const dialogWidthClassName = hasOnlyOneDuplicateCandidate ? "w-[740px]" : "w-[1100px]";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className={`${dialogWidthClassName} max-w-[calc(100vw-2rem)]`}
        hideCloseButton
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t("stakeholders.duplicateCheck.modal.title")}</DialogTitle>
          <DialogDescription className="sr-only"></DialogDescription>
        </DialogHeader>

        <div className="font-light whitespace-pre-line">
          {t("stakeholders.duplicateCheck.modal.description", {
            count: stakeholder.duplicateCandidates?.length ?? 0,
          })}
        </div>

        <div className="mt-20">
          <div className="flex flex-row flex-nowrap gap-x-20">
            <CurrentStakeholderCard
              stakeholder={stakeholder}
              countryWithRiskOptions={countryWithRiskOptions}
              customerCompanyNames={
                stakeholder.id ? customerCompaniesByEntity[stakeholder.id]?.companyNames ?? [] : []
              }
              customerCompaniesLoading={
                stakeholder.id
                  ? customerCompaniesByEntity[stakeholder.id]?.isLoading ?? false
                  : false
              }
              showReferenceButton={hasSameCandidate}
              isReferenceSelected={selectedReferenceProfile?.type === "current"}
              onSelectReference={selectCurrentAsReference}
            />

            {visibleCandidates.map((candidate, i) => {
              const absoluteIndex = candidateStartIndex + i;
              const isLeftmostVisibleCandidate = i === 0;
              const isRightmostVisibleCandidate = i === visibleCandidates.length - 1;

              return (
                <div key={candidate.id ?? absoluteIndex}>
                  <DuplicateCandidateCard
                    candidate={candidate}
                    index={absoluteIndex}
                    total={candidates.length}
                    countryWithRiskOptions={countryWithRiskOptions}
                    customerCompanyNames={
                      candidate.id
                        ? customerCompaniesByEntity[candidate.id]?.companyNames ?? []
                        : []
                    }
                    customerCompaniesLoading={
                      candidate.id
                        ? customerCompaniesByEntity[candidate.id]?.isLoading ?? false
                        : false
                    }
                    selectedDecision={
                      candidateDecisions[getCandidateDecisionKey(candidate, absoluteIndex)]
                    }
                    canGoPrev={canGoPrev && isLeftmostVisibleCandidate}
                    canGoNext={canGoNext && isRightmostVisibleCandidate}
                    onPrev={goToPreviousCandidate}
                    onNext={goToNextCandidate}
                    onMarkDifferent={() => {
                      markCandidateAsDifferent(candidate, absoluteIndex);
                    }}
                    onMarkSame={() => {
                      markCandidateAsSame(candidate, absoluteIndex);
                    }}
                    isReferenceSelected={
                      selectedReferenceProfile?.type === "candidate" &&
                      selectedReferenceProfile.key ===
                        getCandidateDecisionKey(candidate, absoluteIndex)
                    }
                    onSelectReference={() => selectCandidateAsReference(candidate, absoluteIndex)}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-20">
          <span className="font-medium">
            {t("stakeholders.duplicateCheck.modal.referenceProfile.title")}
          </span>
          <ul className="list-disc list-inside text-sm font-light">
            <li>{t("stakeholders.duplicateCheck.modal.referenceProfile.sourceDescription")}</li>
            <li>
              <Trans i18nKey="stakeholders.duplicateCheck.modal.referenceProfile.overwriteDescription" />
            </li>
            <li>
              {t("stakeholders.duplicateCheck.modal.referenceProfile.synchronizationDescription")}
            </li>
          </ul>
        </div>

        <DialogFooter className="flex flex-row justify-center gap-x-40 border-t border-border pt-10 mt-20 h-auto">
          <Button
            variant="link"
            onClick={(event) => {
              event.preventDefault();
              setOpen(false);
            }}
            disabled={loading}
          >
            {t("global.form.cancel")}
          </Button>
          <Button
            variant="default"
            className="ml-[20px]"
            onClick={handleSave}
            loading={loading}
            disabled={loading || !hasSameCandidate}
          >
            {t("stakeholders.duplicateCheck.modal.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
